import { create } from 'zustand';
import TRANSLATIONS_ENGLISH from './localization/translations-english';
import { DEFAULT_LANGUAGE } from './config';

export const LANGUAGES = {
  EN: {
    value: 'en',
    label: 'English',
  },
  ES: {
    value: 'es',
    label: 'Spanish (Español)',
  },
  KO: {
    value: 'ko',
    label: 'Korean (한국어)',
  },
  HI: {
    value: 'hi',
    label: 'Hindi (हिंदी)',
  },
  JA: {
    value: 'ja',
    label: 'Japanese (日本語)',
  },
  RU: {
    value: 'ru',
    label: 'Russian (Русский)',
  },
  AR: {
    value: 'ar',
    label: 'Arabic (العربية)',
  },
  FR: {
    value: 'fr',
    label: 'French (Français)',
  },
  PL: {
    value: 'pl',
    label: 'Polish (Polski)',
  },
};

/** Eager-loaded default locale; all other locales are async chunks on web. */
const LOADED = {
  [DEFAULT_LANGUAGE]: TRANSLATIONS_ENGLISH,
};

const LOCALE_LOADERS = {
  es: () => import('./localization/translations-spanish'),
  ko: () => import('./localization/translations-south-korean'),
  hi: () => import('./localization/translations-hindi'),
  ja: () => import('./localization/translations-japanese'),
  ru: () => import('./localization/translations-russian'),
  ar: () => import('./localization/translations-arabic'),
  fr: () => import('./localization/translations-french'),
  pl: () => import('./localization/translations-polish'),
};

const inFlight = {};

export const useLocaleStore = create((set, get) => ({
  loaded: { [DEFAULT_LANGUAGE]: true },
  markLoaded: (language) => {
    const lang = normalizeLanguage(language);
    if (!lang) return;
    if (get().loaded[lang]) return;
    set((state) => ({
      loaded: { ...state.loaded, [lang]: true },
    }));
  },
  isLoaded: (language) => {
    const lang = normalizeLanguage(language) || DEFAULT_LANGUAGE;
    return Boolean(get().loaded[lang]);
  },
}));

function normalizeLanguage(language) {
  if (typeof language !== 'string') return '';
  return language.trim().toLowerCase();
}

function unwrapLocaleModule(mod) {
  if (!mod) return null;
  return mod.default || mod;
}

/**
 * Synchronous locale lookup. Returns English until a non-English locale chunk
 * has finished loading (prevents hydration mismatch on first paint).
 */
export function getLocaleData(language) {
  const lang = normalizeLanguage(language) || DEFAULT_LANGUAGE;
  return LOADED[lang] || LOADED[DEFAULT_LANGUAGE];
}

export function isLocaleLoaded(language) {
  const lang = normalizeLanguage(language) || DEFAULT_LANGUAGE;
  return Boolean(LOADED[lang]);
}

/**
 * Load a locale dictionary. English is always available; other locales are
 * fetched via dynamic import (separate Metro chunks on web).
 */
export async function loadLocale(language) {
  const lang = normalizeLanguage(language) || DEFAULT_LANGUAGE;

  if (lang === DEFAULT_LANGUAGE) {
    useLocaleStore.getState().markLoaded(DEFAULT_LANGUAGE);
    return LOADED[DEFAULT_LANGUAGE];
  }

  if (LOADED[lang]) {
    useLocaleStore.getState().markLoaded(lang);
    return LOADED[lang];
  }

  const loader = LOCALE_LOADERS[lang];
  if (!loader) {
    return LOADED[DEFAULT_LANGUAGE];
  }

  if (!inFlight[lang]) {
    inFlight[lang] = loader()
      .then((mod) => {
        const data = unwrapLocaleModule(mod);
        if (!data || typeof data !== 'object') {
          throw new Error(`Locale "${lang}" module did not export translations`);
        }
        LOADED[lang] = data;
        useLocaleStore.getState().markLoaded(lang);
        return data;
      })
      .catch((err) => {
        delete inFlight[lang];
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.warn(`[i18n] Failed to load locale "${lang}"`, err);
        }
        return LOADED[DEFAULT_LANGUAGE];
      });
  }

  return inFlight[lang];
}

/** Preload every supported locale (dev/audit tooling only — not used in prod). */
export async function preloadAllLocales() {
  await Promise.all(
    Object.values(LANGUAGES).map((entry) => loadLocale(entry.value)),
  );
}
